import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, NavLink, useLocation } from 'react-router-dom';
import "../../styles/list_files/list_files.css";
import DashboardSidebar from '../DashboardSideBar';
import { fetchUploadedFiles } from './DocsManagement';
import Header from '../Header';
import Loading from '../Loading';
import { useAuth } from '../context/AuthContext';
import FilePagination from '../Pagination.jsx';
import ConfirmationModal from '../ConfirmationModal.jsx';
import {
    deleteFiles,
    deleteFile,
    handleBulkDownload,
    handleSecureDownload,
    sortFiles,
    previewFile
} from '../../utils/fileUtils.js';

const tusApi = process.env.REACT_APP_TUS_SERVER;

const FILE_STATUS = {
    CHECKING: 'checking',
    DONE: 'done',
    NOT_ACCEPTED: 'not_accepted',
    INFECTED: 'infected',
    PROCESSING: 'incomplete',
};

// Get file status based on logic
function getFileStatus({ inQuarantine, completed, infected }) {
    if (infected) {
        return FILE_STATUS.INFECTED;
    }

    if (completed) {
        if (inQuarantine) return FILE_STATUS.CHECKING; // quarantine but completed
        return FILE_STATUS.DONE;
    }

    // If not completed
    return FILE_STATUS.PROCESSING; // always PROCESSING for incomplete files
}
// React component to render file status
function FileStatus({ inQuarantine, completed, infected }) {
    const { t } = useTranslation();
    const normalizedStatus = getFileStatus({ inQuarantine, completed, infected });

    switch (normalizedStatus) {
        case FILE_STATUS.CHECKING:
            return <span className="file-status checking">{t('checking')}</span>;
        case FILE_STATUS.DONE:
            return <span className="file-status done">{t('done')}</span>;
        case FILE_STATUS.NOT_ACCEPTED:
            return <span className="file-status not-accepted">{t('not_accepted')}</span>;
        case FILE_STATUS.INFECTED:
            return <span className="file-status infected">{t('infected')}</span>;
        case FILE_STATUS.PROCESSING:
            return <span className="file-status processing">{t('incomplete')}</span>;
        default:
            return <span className="file-status unknown">{t('unknown')}</span>;
    }
}

export const NoFilesIllustration = (props = {}) => {
    return (
        <svg
            width={props.width || "138"}
            height={props.height || "131"}
            viewBox="0 0 138 131"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label={props["aria-label"] || "No files illustration"}
            {...props}
        >
            <path d="M16.6959 96.7856C16.6959 101.22 14.9456 104.962 8.59793 104.962C4.12247 104.962 0.5 101.22 0.5 96.7856C0.5 92.3515 4.12247 88.7625 8.59793 88.7625C13.0734 88.7625 16.6959 92.3515 16.6959 96.7856Z" fill="#E7EFF7" />
            <path d="M80.106 6.61554H41.7212L48.523 123.861H88.3591L80.106 6.61554Z" fill="#E7EFF7" />
            <path d="M39.3834 81.5574L36.4478 84.4482L39.3834 87.3391L42.8508 84.3379L39.3834 81.5574Z" fill="#E7EFF7" />
            <path d="M62.7576 61.9612C61.6831 61.9612 60.7858 61.6744 60.243 61.1337C59.7888 60.6924 59.5783 60.1076 59.6337 59.4235C59.7445 57.8787 61.96 57.1395 62.8906 56.8967L62.4475 52.2515C62.4253 52.0198 62.6025 51.8101 62.8352 51.7881C63.0789 51.777 63.2783 51.9425 63.3005 52.1742L63.7768 57.1836C63.799 57.4043 63.6549 57.5919 63.4334 57.636C62.6912 57.7905 60.5531 58.4525 60.4867 59.4786C60.4534 59.909 60.5753 60.251 60.8412 60.5158C61.3064 60.9682 62.2148 61.1668 63.2783 61.0675C63.5109 61.0565 63.7214 61.222 63.7436 61.4537C63.7657 61.6854 63.5885 61.895 63.3558 61.9171C63.1454 61.9612 62.946 61.9612 62.7576 61.9612Z" fill="#202020" />
            <path d="M58.0274 53.5866L58.0607 54.6789C58.105 56.0912 55.9005 56.1685 55.8562 54.7451L55.8229 53.6528C55.7786 52.2404 57.9831 52.1632 58.0274 53.5866Z" fill="#202020" />
            <path d="M72.8501 53.5866L72.8833 54.6789C72.9276 56.0912 70.7231 56.1685 70.6788 54.7451L70.6456 53.6528C70.5902 52.2404 72.8057 52.1632 72.8501 53.5866Z" fill="#202020" />
            <path d="M69.8587 48.7648C70.7671 48.7758 71.6533 48.842 72.5396 49.0296C73.2375 49.173 72.8719 50.2102 72.1851 50.0668C71.3985 49.9013 70.5788 49.8682 69.7812 49.8571C69.0722 49.8461 69.1497 48.7538 69.8587 48.7648Z" fill="#202020" />
            <path d="M54.0171 50.6736C54.5489 50.2543 55.1582 49.9233 55.6456 49.4709C56.1662 48.9965 57.0082 49.7026 56.4875 50.1881C55.9114 50.7178 55.2468 51.1039 54.6486 51.5784C54.0947 52.0197 53.4743 51.115 54.0171 50.6736Z" fill="#202020" />
            <path d="M79.6958 36.3517C79.8288 36.3517 79.9617 36.2745 80.0393 36.1531C80.1611 35.9545 80.1057 35.6897 79.9063 35.5683C76.4832 33.4388 71.6976 32.5009 65.6602 32.7547C59.1796 33.0306 54.6266 34.796 52.123 37.9847C51.979 38.1723 52.0122 38.4371 52.2005 38.5806C52.2781 38.6357 52.3667 38.6688 52.4664 38.6688C52.5772 38.6688 52.6769 38.6247 52.7544 38.5475C52.7544 38.5916 52.7544 38.6357 52.7544 38.6688C52.7212 39.2316 52.7101 39.7943 52.699 40.357C60.1877 39.1874 71.188 40.346 74.1791 41.4052C76.8599 42.3431 78.2668 43.7334 78.8872 46.4808C79.5407 49.3827 78.8428 54.1934 80.4048 55.683C80.427 55.7051 80.427 55.7161 80.4491 55.7161V55.683L79.6958 36.3517Z" fill="#202020" />
            <path d="M70.3459 82.2525C70.2572 82.1421 70.1132 82.0759 69.9692 82.098C69.8917 82.109 61.6054 82.9697 56.8862 80.5202C55.7231 79.9133 54.7814 78.9975 54.0281 77.7286C53.9063 77.53 53.6404 77.4638 53.441 77.5851C53.2416 77.7065 53.1751 77.9713 53.297 78.1699C54.1389 79.5712 55.1802 80.5864 56.4874 81.2705C57.9829 82.0428 59.7886 82.5062 61.5833 82.7821C61.5389 82.9035 61.5611 83.0359 61.6276 83.1462L64.2641 87.1625C64.3416 87.2839 64.4746 87.3501 64.6186 87.3501C64.6518 87.3501 64.674 87.3501 64.7072 87.3391C64.8845 87.306 69.1716 86.3902 70.4123 82.6497C70.4566 82.5173 70.4345 82.3628 70.3459 82.2525Z" fill="#202020" />
            <path d="M71.7308 70.8435C72.4509 70.2477 73.0601 69.5194 73.5254 68.7029C73.8134 68.2064 73.038 67.732 72.761 68.2395C72.329 68.9898 71.7862 69.6408 71.1105 70.1925C70.6673 70.5676 71.2877 71.2076 71.7308 70.8435Z" fill="#202020" />
            <path d="M63.3448 104.695C63.3115 104.695 63.2783 104.695 63.2451 104.684C63.1011 104.651 62.9903 104.541 62.946 104.397L56.8531 85.2758C56.7867 85.0772 56.8864 84.8565 57.0747 84.7572L61.8049 82.5284C62.0043 82.4401 62.237 82.5063 62.3478 82.6828L71.3208 96.6737C71.4316 96.8392 71.3984 97.071 71.2544 97.2034L63.6439 104.563C63.5663 104.651 63.4555 104.695 63.3448 104.695ZM57.7726 85.3641L63.5442 103.482L70.4125 96.8392L61.8382 83.4552L57.7726 85.3641Z" fill="#202020" />
            <path d="M72.9159 124.28C72.6832 124.28 72.4838 124.093 72.4838 123.85V101.562L70.5563 97.0709C70.4677 96.8503 70.5674 96.6075 70.7779 96.5082C70.9994 96.4199 71.2431 96.5192 71.3428 96.7289L73.3036 101.308C73.3258 101.363 73.3368 101.418 73.3368 101.473V123.85C73.3368 124.093 73.1485 124.28 72.9159 124.28Z" fill="#202020" />
            <path d="M82.2107 105.589C82.0777 105.589 81.9559 105.534 81.8673 105.424L75.4532 97.1702C75.3424 97.0378 75.3313 96.8503 75.4199 96.6958L81.1361 86.7212C81.258 86.5115 81.5128 86.4453 81.7232 86.5667C81.9226 86.6881 82.0002 86.9419 81.8783 87.1515L76.3061 96.8723L81.8451 104.011C81.9891 104.199 82.6427 105.357 82.4544 105.501C82.3879 105.556 82.2993 105.589 82.2107 105.589Z" fill="#202020" />
            <path d="M72.9158 101.904C72.8383 101.904 72.7607 101.882 72.6943 101.838C72.4949 101.716 72.4284 101.451 72.5613 101.253L75.4416 96.6737C75.5635 96.4751 75.8293 96.4199 76.0287 96.5413C76.2281 96.6627 76.2946 96.9275 76.1617 97.1261L73.2814 101.705C73.1928 101.838 73.0598 101.904 72.9158 101.904Z" fill="#202020" />
            <path d="M53.8069 87.3612C53.6518 87.3612 53.5078 87.2729 53.4303 87.1294C53.3195 86.9198 53.397 86.666 53.6075 86.5557L57.0527 84.7682C57.2632 84.6579 57.518 84.7351 57.6288 84.9447C57.7396 85.1544 57.662 85.4082 57.4515 85.5185L54.0063 87.306C53.9398 87.3391 53.8734 87.3612 53.8069 87.3612Z" fill="#202020" />
            <path d="M55.2357 131H32.7032C32.4706 131 32.2823 130.823 32.2712 130.592L27.7847 34.3766C27.7736 34.1449 27.9508 33.9463 28.1835 33.9353L52.2779 32.6112C52.3886 32.6002 52.5105 32.6443 52.588 32.7216C52.6767 32.7988 52.721 32.9092 52.721 33.0195L55.6566 130.559C55.6566 130.669 55.6123 130.779 55.5348 130.868C55.4572 130.945 55.3464 131 55.2357 131ZM33.1021 130.139H54.7925L51.8901 33.4719L28.6598 34.7518L33.1021 130.139Z" fill="#202020" />
            <path d="M113.173 126.929H83.1518C82.9192 126.929 82.7309 126.752 82.7198 126.52L78.2222 9.55055C78.2222 9.44021 78.2554 9.31884 78.344 9.2416C78.4216 9.15333 78.5324 9.10919 78.6542 9.10919H110.304C110.536 9.10919 110.725 9.29677 110.736 9.52848L111.123 24.9648L113.605 126.498C113.605 126.609 113.561 126.719 113.483 126.807C113.394 126.884 113.284 126.929 113.173 126.929ZM83.5617 126.079H112.73L110.259 24.9869L109.883 9.9588H79.0862L83.5617 126.079Z" fill="#202020" />
            <path d="M137.068 124.28H113.173C112.94 124.28 112.752 124.093 112.741 123.861L110.259 24.9758C110.259 24.8655 110.304 24.7552 110.381 24.6669C110.459 24.5896 110.569 24.5345 110.691 24.5345L136.049 24.5455C136.281 24.5455 136.47 24.7331 136.481 24.9648L137.5 123.828C137.5 123.938 137.455 124.049 137.378 124.126C137.289 124.236 137.179 124.28 137.068 124.28ZM113.594 123.431H136.636L135.628 25.4172L111.134 25.4062L113.594 123.431Z" fill="#202020" />
            <path d="M32.3931 124.28H9.62802C9.39538 124.28 9.20706 124.104 9.19598 123.872L3.6792 1.20895C3.6792 1.08758 3.72351 0.977237 3.80106 0.888966C3.88968 0.800695 3.98938 0.756563 4.12231 0.767597L43.5374 2.19096C43.77 2.202 43.9473 2.38957 43.9473 2.62128V33.516C43.9473 33.7477 43.759 33.9464 43.5153 33.9464C43.2715 33.9464 43.0832 33.7588 43.0832 33.516V3.0185L4.54327 1.62823L10.0157 123.431H32.3709C32.6035 123.431 32.8029 123.618 32.8029 123.861C32.8029 124.104 32.6257 124.28 32.3931 124.28Z" fill="#202020" />
            <path d="M83.0523 124.28H55.2358C55.0031 124.28 54.8037 124.093 54.8037 123.85C54.8037 123.607 54.992 123.42 55.2358 123.42H83.0523C83.2849 123.42 83.4843 123.607 83.4843 123.85C83.4843 124.093 83.2849 124.28 83.0523 124.28Z" fill="#202020" />

        </svg >
    );
}

export default function ListFiles({ onDeleteSuccess, filesPerPage = 10 }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const { token, loading, user: currentUser } = useAuth();
    const [files, setUploadedFiles] = useState([]);
    const { lang, username: paramUsername } = useParams();
    const [filesLoading, setFilesLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState("asc");
    const username = paramUsername;
    const [currentPage, setCurrentPage] = useState(1);
    const [currentItems, setCurrentItems] = useState([]);
    const [pageSize, setPageSize] = useState(filesPerPage);
    const { state } = useLocation();
    const user = state?.user ?? currentUser;

    // Normalize username fields
    const currentUserName = currentUser?.username ?? currentUser?.userName ?? "";
    const clickedUserName = user?.username ?? user?.userName ?? "";
    const [showModal, setShowModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    // Check if the logged-in user clicked on themselves
    const isOwner =
        currentUserName.trim().toLowerCase() === clickedUserName.trim().toLowerCase();

    const descriptionText = isOwner
        ? t("doc-content-description")
        : t("doc-non-add-content-description");

    const showAddButton = isOwner;
    console.log("showAddButton:", showAddButton);


    const loadFiles = async () => {
        if (!username || !token) return;
        setFilesLoading(true);

        try {
            const files = await fetchUploadedFiles(tusApi, username, token);
            setUploadedFiles(files);
        } catch (error) {
            console.error("Failed to fetch uploaded files:", error);
        } finally {
            setFilesLoading(false);
        }
    };
    useEffect(() => {
        if (username && token) {
            loadFiles();
        }
    }, [username, token, lang]);

    useEffect(() => {
    }, [files]);

    const filteredFiles = useMemo(() => {
        return files
            .filter(file => file.fileName) // only files with fileName
            .filter(file => file.fileName.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [files, searchTerm]);

    // ✅ Calculate total pages after filtering
    const totalPages = Math.ceil(filteredFiles.length / pageSize);

    // ✅ Update current items when page or page size changes
    useEffect(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setCurrentItems(filteredFiles.slice(startIndex, endIndex));
    }, [currentPage, pageSize, filteredFiles]);

    // ✅ Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);


    const handlePageChange = (page, newPageSize = pageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(page);
    };
    const totalSizeBytes = files
        .filter(file => file.completed)
        .reduce((sum, file) => sum + file.fileSize, 0); const totalSizeMB = totalSizeBytes / (1024 * 1024);
    const maxSize = 10;


    const formatFileSize = (size) => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };



    const handleSortFiles = (key) => {
        const { sorted, newSortOrder } = sortFiles(files, key, sortOrder);
        setUploadedFiles(sorted);
        setSortOrder(newSortOrder);
    };

    const restartUpload = () => {
        navigate(`/lang/${lang}/upload`);
    };


    const toggleFileSelection = (fileId) => {
        setSelectedFiles((prevSelected) =>
            prevSelected.includes(fileId)
                ? prevSelected.filter((id) => id !== fileId)
                : [...prevSelected, fileId]
        );
    };

    // Delete a single file
    const handleDeleteFile = async (file) => {
        if (!file) return;

        // if (!window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) {
        //     return;
        // }

        setFilesLoading(true);
        try {
            await deleteFile(file, tusApi, token);
            await loadFiles();
            if (typeof onDeleteSuccess === "function") onDeleteSuccess();
        } catch (err) {
            console.error(`Failed to delete file ${file.fileName}:`, err);
        } finally {
            setFilesLoading(false);
        }
    };

    // Delete selected files
    const handleDeleteSelectedFiles = async () => {
        if (!selectedFiles.length) return console.log("No files selected.");

        setFilesLoading(true);
        try {
            // if (!window.confirm(`Delete ${selectedFiles.length} selected file(s)?`)) {
            //     return setFilesLoading(false);
            // }

            const filesToDelete = selectedFiles
                .map(fileId => files.find(f => f.fileId === fileId))
                .filter(Boolean);

            await deleteFiles(filesToDelete, tusApi, token, loadFiles, onDeleteSuccess);
            setSelectedFiles([]);
        } catch (err) {
            console.error("Bulk delete error:", err);
        } finally {
            setFilesLoading(false);
        }
    };

    // Modal confirm button
    const handleConfirmDelete = () => {
        if (!fileToDelete) return;

        if (Array.isArray(fileToDelete)) {
            handleDeleteSelectedFiles(); // bulk delete
        } else {
            handleDeleteFile(fileToDelete); // single delete
        }
        closeModal();
    };

    // Open modal for single file
    const openDeleteModal = (file) => {
        setFileToDelete(file);
        setShowModal(true);
    };

    // Open modal for bulk delete
    const openDeleteSelectedModal = () => {
        if (!selectedFiles.length) return;
        setFileToDelete(selectedFiles); // array of fileIds
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        setShowModal(false);
        setFileToDelete(null);
    };


    if (loading || filesLoading) return <Loading />;
    return (
        <div className="uploadpage">
            <Header hideHamburger={false} profileHide={true} hideNav={true} />
            <div className="dashboard">
                <DashboardSidebar
                    uploadedFiles={files}
                    totalSizeMB={totalSizeMB}
                    maxSize={maxSize}
                />
                <section className="list-container">
                    <div className="head-container">
                        <div className="head">
                            <h2>{t('uploaded-doc')}</h2>
                            <p>
                                {t('uploaded-doc-description')}
                            </p>
                        </div>
                        <div className="contents">
                            <div className="content">
                                <div className="search-bar-container">
                                    <div className="search">
                                        <input
                                            type="text"
                                            placeholder={t('search-doc')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button className="search-button">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#9ca3af">
                                                <path d="M380-320q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l224 224q11 11 11 28t-11 28q-11 11-28 11t-28-11L532-372q-30 24-69 38t-83 14Zm0-80q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                                            </svg>
                                        </button>
                                    </div>
                                    {selectedFiles.length > 0 && (

                                        <div className="bulk-actions">

                                            <button
                                                className='delete-btn'
                                                type='button'
                                                onClick={openDeleteSelectedModal}
                                                disabled={filesLoading}
                                            >
                                                {t('del-selected')}
                                            </button>

                                            <button
                                                className='download-btn'
                                                type='button'
                                                onClick={() => handleBulkDownload(selectedFiles, files, tusApi, token)}
                                                disabled={filesLoading}
                                            >
                                                {t('down-selected')}
                                            </button>
                                        </div>
                                    )}
                                </div>


                                {
                                    files.length === 0 ? (
                                        <div className="non-data">
                                            <div className="img-head">
                                                {NoFilesIllustration({ width: "150", height: "140" })}
                                            </div>
                                            <div className="non-content">
                                                <h4>{t("doc-content")}</h4>
                                                <p>{descriptionText}</p>

                                                {showAddButton && (
                                                    <NavLink
                                                        to={`/lang/${lang}/upload`}
                                                        className={({ isActive }) => `add-button ${isActive ? "active" : ""}`}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="20px"
                                                            height="20px"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M12 4.75c.69 0 1.25.56 1.25 1.25v4.75H18a1.25 1.25 0 1 1 0 2.5h-4.75V18a1.25 1.25 0 1 1-2.5 0v-4.75H6a1.25 1.25 0 1 1 0-2.5h4.75V6c0-.69.56-1.25 1.25-1.25"
                                                            />
                                                        </svg>
                                                        <span>{t("add-doc")}</span>
                                                    </NavLink>
                                                )}
                                            </div>

                                        </div>
                                    ) : (
                                        <>
                                            <div className="file-table-wrapper">
                                                <table className="file-table">
                                                    <thead>
                                                        <tr>
                                                            <th>
                                                                <div className="file-name">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedFiles.length === files.length && filteredFiles.length > 0}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedFiles(filteredFiles.map(file => file.fileId));
                                                                            } else {
                                                                                setSelectedFiles([]);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <div className="head-file" onClick={() => handleSortFiles("fileName")} style={{ cursor: "pointer" }}>
                                                                        <span>{t('file-name')}</span>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            height="20"
                                                                            viewBox="0 -960 960 960"
                                                                            width="20"
                                                                            fill="#9ca3af"
                                                                        >
                                                                            <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="file-name">
                                                                    <div className="head-file" onClick={() => handleSortFiles("fileDate")} style={{ cursor: "pointer" }}>
                                                                        <span>{t('uploaded-date')}</span>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            height="20"
                                                                            viewBox="0 -960 960 960"
                                                                            width="20"
                                                                            fill="#9ca3af"
                                                                        >
                                                                            <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                        </svg>
                                                                    </div>

                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="file-name">
                                                                    <div className="head-file" onClick={() => handleSortFiles("fileSize")} style={{ cursor: "pointer" }}>
                                                                        <span>{t('file-size')}</span>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            height="20"
                                                                            viewBox="0 -960 960 960"
                                                                            width="20"
                                                                            fill="#9ca3af"
                                                                        >
                                                                            <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                        </svg>
                                                                    </div>

                                                                </div>
                                                            </th>
                                                            {/* <th>
                                                                <div className="file-name">
                                                                    <div className="head-file" onClick={() => handleSortFiles("username")} style={{ cursor: "pointer" }}>
                                                                        <span>{t('owner')}</span>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            height="20"
                                                                            viewBox="0 -960 960 960"
                                                                            width="20"
                                                                            fill="#9ca3af"
                                                                        >
                                                                            <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                        </svg>
                                                                    </div>

                                                                </div>
                                                            </th> */}

                                                            <th>{t('status')}</th>
                                                            <th>{t('action')}</th>
                                                        </tr>

                                                    </thead>
                                                    <tbody>
                                                        {currentItems.map((file) => (
                                                            <tr key={file.fileId}>
                                                                <td>
                                                                    <div className="file-name">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedFiles.includes(file.fileId)}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFileSelection(file.fileId);
                                                                            }}
                                                                        />

                                                                        {/* Clickable filename for preview */}
                                                                        <span
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                previewFile(file.fileId, token, tusApi);
                                                                            }}
                                                                        >
                                                                            {file.fileName}
                                                                        </span>
                                                                    </div>

                                                                </td>
                                                                <td>{file.fileDate}</td>
                                                                <td>{formatFileSize(file.fileSize)}</td>
                                                                {/* <td>{file.username}</td> */}
                                                                <td className="status">
                                                                    <FileStatus inQuarantine={file.quarantine} completed={file.completed} infected={file.infected} />
                                                                </td>

                                                                <td className="actions">

                                                                    {file.completed ? (
                                                                        <button
                                                                            className="download-button"
                                                                            title="Download"
                                                                            onClick={() => handleSecureDownload(username, file.fileId, file.fileName, tusApi, token)}
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M12 15.248q-.161 0-.298-.053t-.267-.184l-2.62-2.619q-.146-.146-.152-.344t.152-.363q.166-.166.357-.169q.192-.003.357.163L11.5 13.65V5.5q0-.213.143-.357T12 5t.357.143t.143.357v8.15l1.971-1.971q.146-.146.347-.153t.366.159q.16.165.163.354t-.162.353l-2.62 2.62q-.13.13-.267.183q-.136.053-.298.053M6.616 19q-.691 0-1.153-.462T5 17.384v-1.923q0-.213.143-.356t.357-.144t.357.144t.143.356v1.923q0 .231.192.424t.423.192h10.77q.23 0 .423-.192t.192-.424v-1.923q0-.213.143-.356t.357-.144t.357.144t.143.356v1.923q0 .691-.462 1.153T17.384 19z" /></svg>

                                                                        </button>
                                                                    ) : (
                                                                        <button className="restart-button" title="Restart" onClick={() => restartUpload(file)}>
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M11 19.912q-2.602-.356-4.301-2.32Q5 15.626 5 13q0-1.4.554-2.682t1.523-2.272l.714.713q-.893.831-1.342 1.927T6 13q0 2.2 1.41 3.868q1.41 1.669 3.59 2.044zm2 .038v-1q2.156-.477 3.578-2.123T18 13q0-2.5-1.75-4.25T12 7h-.633l1.6 1.6l-.708.708L9.452 6.5l2.808-2.808l.707.708l-1.6 1.6H12q2.927 0 4.964 2.036T19 13q0 2.621-1.709 4.566Q15.583 19.512 13 19.95" /></svg>
                                                                        </button>
                                                                    )}


                                                                    <button
                                                                        className="delete-button"
                                                                        title="Delete"
                                                                        onClick={() => { openDeleteModal(file) }}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M7.616 20q-.672 0-1.144-.472T6 18.385V6H5V5h4v-.77h6V5h4v1h-1v12.385q0 .69-.462 1.153T16.384 20zM17 6H7v12.385q0 .269.173.442t.443.173h8.769q.23 0 .423-.192t.192-.424zM9.808 17h1V8h-1zm3.384 0h1V8h-1zM7 6v13z" /></svg>

                                                                    </button>

                                                                </td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            <FilePagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                totalItems={files?.length}
                                                onPageChange={handlePageChange}
                                            />
                                        </>
                                    )
                                }
                                <ConfirmationModal
                                    isOpen={showModal}
                                    onClose={closeModal}
                                    onConfirm={handleConfirmDelete}
                                    header={
                                        Array.isArray(fileToDelete)
                                            ? `${t("head-del-file")} ${fileToDelete.length} ${t("files")}?`
                                            : `${t("head-del-file")} ${fileToDelete?.fileName} ?`
                                    }
                                    message={t("paragraph-del-file")}
                                />
                            </div>
                        </div>
                    </div>
                </section >
            </div >
        </div >
    );
}